import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from './supabase';

const STEPS = {
  INFO: 'info',
  QUESTIONS: 'questions',
  CONTACT: 'contact',
  SUCCESS: 'success',
};

export default function ApplyPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Flow state
  const [step, setStep] = useState(STEPS.INFO);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select('*')
          .eq('id', jobId)
          .single();
        if (error) throw error;
        setJob(data);
      } catch (err) {
        setError('Stelle nicht gefunden.');
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId]);

  const questions = job?.questions || [];

  const handleSelectAnswer = (questionIdx, value) => {
    setAnswers(prev => ({ ...prev, [questionIdx]: value }));
  };

  const handleNextQuestion = () => {
    if (questionIndex < questions.length - 1) {
      setQuestionIndex(i => i + 1);
    } else {
      setStep(STEPS.CONTACT);
    }
  };

  const handleSubmit = async () => {
    if (!contact.name || !contact.email) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const formattedAnswers = questions.map((q, i) => ({
        question: q.question,
        answer: answers[i] ?? '',
      }));
      const { error } = await supabase.from('applications').insert({
        job_id: jobId,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        answers: formattedAnswers,
        status: 'new',
      });
      if (error) throw error;
      setStep(STEPS.SUCCESS);
    } catch (err) {
      setSubmitError('Fehler beim Absenden: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Loading / Error ───
  if (loading) return (
    <div style={styles.centered}>
      <div style={{ fontSize: '2rem', marginBottom: '12px' }}>⏳</div>
      <p style={{ color: '#9ca3af' }}>Wird geladen...</p>
    </div>
  );

  if (error || !job) return (
    <div style={styles.centered}>
      <div style={{ fontSize: '3rem', marginBottom: '12px' }}>😕</div>
      <p style={{ color: '#6b7280' }}>{error || 'Stelle nicht gefunden.'}</p>
    </div>
  );

  const primaryColor = job.brand_color || '#667eea';

  // ─── INFO (Stellenbeschreibung) ───
  if (step === STEPS.INFO) return (
    <div style={styles.page}>
      {/* Hero */}
      <div style={{ ...styles.hero, background: `linear-gradient(135deg, ${primaryColor} 0%, #764ba2 100%)` }}>
        {job.logo_url && (
          <img src={job.logo_url} alt="Logo" style={styles.logo} />
        )}
        <div style={styles.heroCompany}>{job.company}</div>
        <h1 style={styles.heroTitle}>{job.position}</h1>
        <div style={styles.heroBadges}>
          {job.location && <span style={styles.badge}>📍 {job.location}</span>}
          {job.contract_type && <span style={styles.badge}>📄 {job.contract_type}</span>}
          {job.work_time && <span style={styles.badge}>⏰ {job.work_time}</span>}
          {job.salary && <span style={styles.badge}>💶 {job.salary}</span>}
        </div>
      </div>

      <div style={styles.content}>
        {/* Beschreibung */}
        {job.description && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Über die Stelle</h2>
            <p style={styles.text}>{job.description}</p>
          </section>
        )}

        {/* Aufgaben */}
        {job.tasks?.length > 0 && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Deine Aufgaben</h2>
            <ul style={styles.list}>
              {job.tasks.map((t, i) => (
                <li key={i} style={styles.listItem}>
                  <span style={{ color: primaryColor, fontWeight: '700', marginRight: '8px' }}>→</span>
                  {t}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Benefits */}
        {job.benefits?.length > 0 && (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Was wir bieten</h2>
            <div style={styles.benefitGrid}>
              {job.benefits.map((b, i) => (
                <div key={i} style={styles.benefitItem}>
                  <span style={{ color: primaryColor, fontSize: '1.1rem' }}>✓</span>
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <button
          onClick={() => questions.length > 0 ? setStep(STEPS.QUESTIONS) : setStep(STEPS.CONTACT)}
          style={{ ...styles.primaryBtn, background: `linear-gradient(135deg, ${primaryColor} 0%, #764ba2 100%)` }}
        >
          Jetzt bewerben →
        </button>

        <div style={styles.poweredBy}>Powered by recruifly</div>
      </div>
    </div>
  );

  // ─── FRAGEN (eine nach der anderen) ───
  if (step === STEPS.QUESTIONS) {
    const q = questions[questionIndex];
    const currentAnswer = answers[questionIndex];
    const progress = ((questionIndex) / questions.length) * 100;

    return (
      <div style={styles.page}>
        {/* Progress Bar */}
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: `${progress}%`, background: primaryColor }} />
        </div>

        <div style={styles.content}>
          {/* Counter */}
          <div style={{ textAlign: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
              Frage {questionIndex + 1} von {questions.length}
            </span>
          </div>

          {/* Frage */}
          <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#111827', textAlign: 'center', marginBottom: '32px', lineHeight: 1.4 }}>
            {q.question}
          </h2>

          {/* Antworten */}
          {q.type === 'select' && q.options ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {q.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => {
                    handleSelectAnswer(questionIndex, opt);
                  }}
                  style={{
                    padding: '16px 20px',
                    borderRadius: '12px',
                    border: currentAnswer === opt
                      ? `2.5px solid ${primaryColor}`
                      : '2px solid #e5e7eb',
                    background: currentAnswer === opt ? `${primaryColor}15` : 'white',
                    color: currentAnswer === opt ? primaryColor : '#374151',
                    fontSize: '1rem',
                    fontWeight: currentAnswer === opt ? '700' : '500',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                    boxShadow: currentAnswer === opt ? `0 0 0 3px ${primaryColor}20` : 'none',
                  }}
                >
                  {opt}
                </button>
              ))}
              <button
                onClick={handleNextQuestion}
                disabled={!currentAnswer}
                style={{
                  ...styles.primaryBtn,
                  background: currentAnswer
                    ? `linear-gradient(135deg, ${primaryColor} 0%, #764ba2 100%)`
                    : '#e5e7eb',
                  color: currentAnswer ? 'white' : '#9ca3af',
                  marginTop: '8px',
                  cursor: currentAnswer ? 'pointer' : 'not-allowed',
                }}
              >
                {questionIndex < questions.length - 1 ? 'Weiter →' : 'Fertig →'}
              </button>
            </div>
          ) : (
            <div>
              <textarea
                value={currentAnswer || ''}
                onChange={e => handleSelectAnswer(questionIndex, e.target.value)}
                placeholder="Deine Antwort..."
                rows={4}
                style={styles.textarea}
              />
              <button
                onClick={handleNextQuestion}
                style={{ ...styles.primaryBtn, background: `linear-gradient(135deg, ${primaryColor} 0%, #764ba2 100%)` }}
              >
                {questionIndex < questions.length - 1 ? 'Weiter →' : 'Fertig →'}
              </button>
            </div>
          )}

          {questionIndex > 0 && (
            <button
              onClick={() => setQuestionIndex(i => i - 1)}
              style={styles.backBtn}
            >
              ← Zurück
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── KONTAKTDATEN ───
  if (step === STEPS.CONTACT) return (
    <div style={styles.page}>
      <div style={styles.progressBar}>
        <div style={{ ...styles.progressFill, width: '90%', background: primaryColor }} />
      </div>
      <div style={styles.content}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#111827', marginBottom: '8px' }}>
          Fast geschafft!
        </h2>
        <p style={{ color: '#6b7280', marginBottom: '28px', fontSize: '0.95rem' }}>
          Gib deine Kontaktdaten an, damit wir uns bei dir melden können.
        </p>

        {[
          { key: 'name', label: 'Dein Name *', placeholder: 'Max Mustermann', type: 'text' },
          { key: 'email', label: 'E-Mail *', placeholder: 'max@beispiel.de', type: 'email' },
          { key: 'phone', label: 'Telefon (optional)', placeholder: '+49 123 456789', type: 'tel' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: '16px' }}>
            <label style={styles.label}>{f.label}</label>
            <input
              type={f.type}
              value={contact[f.key]}
              onChange={e => setContact(prev => ({ ...prev, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              style={styles.input}
            />
          </div>
        ))}

        {submitError && (
          <div style={{ padding: '12px', background: '#fee2e2', borderRadius: '8px', color: '#dc2626', fontSize: '0.875rem', marginBottom: '16px' }}>
            {submitError}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting || !contact.name || !contact.email}
          style={{
            ...styles.primaryBtn,
            background: (!contact.name || !contact.email) ? '#e5e7eb'
              : `linear-gradient(135deg, ${primaryColor} 0%, #764ba2 100%)`,
            color: (!contact.name || !contact.email) ? '#9ca3af' : 'white',
            cursor: (!contact.name || !contact.email) ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? '⏳ Wird gesendet...' : 'Jetzt bewerben →'}
        </button>

        <button onClick={() => questions.length > 0 ? setStep(STEPS.QUESTIONS) : setStep(STEPS.INFO)} style={styles.backBtn}>
          ← Zurück
        </button>

        <div style={styles.poweredBy}>Powered by recruifly</div>
      </div>
    </div>
  );

  // ─── SUCCESS ───
  return (
    <div style={styles.centered}>
      <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🎉</div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#111827', marginBottom: '8px' }}>
        Bewerbung abgeschickt!
      </h2>
      <p style={{ color: '#6b7280', maxWidth: '300px', textAlign: 'center', lineHeight: 1.6 }}>
        Vielen Dank, {contact.name}! Wir melden uns so schnell wie möglich bei dir.
      </p>
      <div style={{ marginTop: '32px', fontSize: '0.78rem', color: '#d1d5db' }}>Powered by recruifly</div>
    </div>
  );
}

// ─── Styles ───
const styles = {
  page: {
    minHeight: '100vh',
    background: '#f8f9fc',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  centered: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '24px',
  },
  hero: {
    padding: '48px 24px 40px',
    color: 'white',
    textAlign: 'center',
  },
  logo: {
    height: '48px',
    borderRadius: '8px',
    marginBottom: '16px',
    objectFit: 'contain',
  },
  heroCompany: {
    fontSize: '0.82rem',
    opacity: 0.85,
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    marginBottom: '8px',
    fontWeight: '600',
  },
  heroTitle: {
    fontSize: '1.75rem',
    fontWeight: '800',
    margin: '0 0 16px',
    lineHeight: 1.2,
  },
  heroBadges: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  badge: {
    background: 'rgba(255,255,255,0.2)',
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '0.82rem',
    fontWeight: '500',
  },
  content: {
    maxWidth: '480px',
    margin: '0 auto',
    padding: '32px 20px 48px',
  },
  section: {
    marginBottom: '28px',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: '800',
    color: '#111827',
    marginBottom: '12px',
  },
  text: {
    fontSize: '0.95rem',
    color: '#374151',
    lineHeight: 1.7,
    margin: 0,
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: '0.95rem',
    color: '#374151',
    lineHeight: 1.6,
    marginBottom: '8px',
  },
  benefitGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.875rem',
    color: '#374151',
    fontWeight: '500',
  },
  primaryBtn: {
    display: 'block',
    width: '100%',
    padding: '16px',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '24px',
    textAlign: 'center',
  },
  backBtn: {
    display: 'block',
    width: '100%',
    marginTop: '12px',
    padding: '12px',
    background: 'transparent',
    border: 'none',
    color: '#9ca3af',
    fontSize: '0.9rem',
    cursor: 'pointer',
    textAlign: 'center',
  },
  progressBar: {
    height: '4px',
    background: '#e5e7eb',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  progressFill: {
    height: '100%',
    borderRadius: '2px',
    transition: 'width 0.3s ease',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '13px 14px',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '1rem',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  textarea: {
    width: '100%',
    padding: '13px 14px',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '1rem',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    marginBottom: '0',
  },
  poweredBy: {
    textAlign: 'center',
    marginTop: '32px',
    fontSize: '0.72rem',
    color: '#d1d5db',
  },
};
